/**
 * VSEPR (valence-shell electron-pair repulsion) shapes for AXₙEₘ molecules.
 *
 * `A` is the central atom, `X` a bonded atom, `E` a lone pair on A. A double or triple
 * bond counts as one electron domain. The steric number is n + m; the domains point to
 * the corners of the electron geometry, and the molecular shape is what is left when the
 * lone pairs are removed. The table is the standard one in general-chemistry texts
 * (Zumdahl; Brown et al.; OpenStax Chemistry 2e §7.6, "Molecular Structure and Polarity"):
 * steric numbers 2–6, with lone pairs placed equatorially in a trigonal bipyramid because
 * that minimises 90° repulsions, and trans to each other in an octahedron.
 *
 * Hybridisation labels (sp … sp³d²) are the textbook electron-counting convention. Modern
 * calculations show d orbitals contribute little to bonding in main-group "hypervalent"
 * molecules, so sp³d and sp³d² are labels for the geometry, not proof of d-orbital use.
 */

export type GeometryId =
  | "linear"
  | "trigonalPlanar"
  | "bent"
  | "tetrahedral"
  | "trigonalPyramidal"
  | "trigonalBipyramidal"
  | "seesaw"
  | "tShaped"
  | "octahedral"
  | "squarePyramidal"
  | "squarePlanar";

export type ElectronGeometryId =
  | "linear"
  | "trigonalPlanar"
  | "tetrahedral"
  | "trigonalBipyramidal"
  | "octahedral";

export type Hybridization = "sp" | "sp2" | "sp3" | "sp3d" | "sp3d2";

/** How lone pairs change the bond angles relative to the electron geometry. */
export type AngleTrend = "ideal" | "compressed";

export interface VseprShape {
  /** Bonded atoms. */
  x: number;
  /** Lone pairs on the central atom. */
  e: number;
  steric: number;
  electronGeometry: ElectronGeometryId;
  shape: GeometryId;
  hybridization: Hybridization;
  /** Ideal X–A–X angles of the electron geometry that occur in this shape, in degrees. */
  idealAngles: readonly number[];
  trend: AngleTrend;
  /** True when equal outer atoms give bond dipoles that cancel. */
  symmetric: boolean;
  examples: readonly string[];
}

export const VSEPR_SHAPES: readonly VseprShape[] = [
  {
    x: 2,
    e: 0,
    steric: 2,
    electronGeometry: "linear",
    shape: "linear",
    hybridization: "sp",
    idealAngles: [180],
    trend: "ideal",
    symmetric: true,
    examples: ["CO2", "BeCl2", "HCN"],
  },
  {
    x: 3,
    e: 0,
    steric: 3,
    electronGeometry: "trigonalPlanar",
    shape: "trigonalPlanar",
    hybridization: "sp2",
    idealAngles: [120],
    trend: "ideal",
    symmetric: true,
    examples: ["BF3", "SO3", "NO3-"],
  },
  {
    x: 2,
    e: 1,
    steric: 3,
    electronGeometry: "trigonalPlanar",
    shape: "bent",
    hybridization: "sp2",
    idealAngles: [120],
    trend: "compressed",
    symmetric: false,
    examples: ["SO2", "O3", "NO2-"],
  },
  {
    x: 4,
    e: 0,
    steric: 4,
    electronGeometry: "tetrahedral",
    shape: "tetrahedral",
    hybridization: "sp3",
    idealAngles: [109.5],
    trend: "ideal",
    symmetric: true,
    examples: ["CH4", "CCl4", "NH4+"],
  },
  {
    x: 3,
    e: 1,
    steric: 4,
    electronGeometry: "tetrahedral",
    shape: "trigonalPyramidal",
    hybridization: "sp3",
    idealAngles: [109.5],
    trend: "compressed",
    symmetric: false,
    examples: ["NH3", "PCl3", "H3O+"],
  },
  {
    x: 2,
    e: 2,
    steric: 4,
    electronGeometry: "tetrahedral",
    shape: "bent",
    hybridization: "sp3",
    idealAngles: [109.5],
    trend: "compressed",
    symmetric: false,
    examples: ["H2O", "H2S", "OF2"],
  },
  {
    x: 5,
    e: 0,
    steric: 5,
    electronGeometry: "trigonalBipyramidal",
    shape: "trigonalBipyramidal",
    hybridization: "sp3d",
    idealAngles: [90, 120, 180],
    trend: "ideal",
    symmetric: true,
    examples: ["PCl5", "PF5", "AsF5"],
  },
  {
    x: 4,
    e: 1,
    steric: 5,
    electronGeometry: "trigonalBipyramidal",
    shape: "seesaw",
    hybridization: "sp3d",
    idealAngles: [90, 120, 180],
    trend: "compressed",
    symmetric: false,
    examples: ["SF4", "TeCl4", "XeO2F2"],
  },
  {
    x: 3,
    e: 2,
    steric: 5,
    electronGeometry: "trigonalBipyramidal",
    shape: "tShaped",
    hybridization: "sp3d",
    idealAngles: [90, 180],
    trend: "compressed",
    symmetric: false,
    examples: ["ClF3", "BrF3", "ICl3"],
  },
  {
    x: 2,
    e: 3,
    steric: 5,
    electronGeometry: "trigonalBipyramidal",
    shape: "linear",
    hybridization: "sp3d",
    idealAngles: [180],
    trend: "ideal",
    symmetric: true,
    examples: ["XeF2", "I3-", "KrF2"],
  },
  {
    x: 6,
    e: 0,
    steric: 6,
    electronGeometry: "octahedral",
    shape: "octahedral",
    hybridization: "sp3d2",
    idealAngles: [90, 180],
    trend: "ideal",
    symmetric: true,
    examples: ["SF6", "PF6-", "SeF6"],
  },
  {
    x: 5,
    e: 1,
    steric: 6,
    electronGeometry: "octahedral",
    shape: "squarePyramidal",
    hybridization: "sp3d2",
    idealAngles: [90, 180],
    trend: "compressed",
    symmetric: false,
    examples: ["BrF5", "IF5", "XeOF4"],
  },
  {
    x: 4,
    e: 2,
    steric: 6,
    electronGeometry: "octahedral",
    shape: "squarePlanar",
    hybridization: "sp3d2",
    idealAngles: [90, 180],
    trend: "ideal",
    symmetric: true,
    examples: ["XeF4", "ICl4-", "BrF4-"],
  },
];

export const MAX_BONDED = 6;
export const MAX_LONE_PAIRS = 4;
export const MAX_STERIC = 6;

export type VseprResult =
  | { status: "ok"; shape: VseprShape }
  | { status: "diatomic"; steric: number; electronGeometry: ElectronGeometryId }
  | {
      status: "unsupported";
      reason: "noBonds" | "tooMany" | "notListed";
    };

const ELECTRON_GEOMETRY_BY_STERIC: Record<number, ElectronGeometryId> = {
  2: "linear",
  3: "trigonalPlanar",
  4: "tetrahedral",
  5: "trigonalBipyramidal",
  6: "octahedral",
};

/** Look up the shape for x bonded atoms and e lone pairs. */
export function findShape(x: number, e: number): VseprResult {
  if (!(Number.isInteger(x) && Number.isInteger(e)) || x < 1 || e < 0) {
    return { status: "unsupported", reason: "noBonds" };
  }
  const steric = x + e;
  if (steric > MAX_STERIC) {
    return { status: "unsupported", reason: "tooMany" };
  }
  if (x === 1) {
    // One bonded atom: the molecule is diatomic and linear whatever the lone pairs.
    const electronGeometry = ELECTRON_GEOMETRY_BY_STERIC[Math.max(steric, 2)];
    return { status: "diatomic", steric, electronGeometry };
  }
  const shape = VSEPR_SHAPES.find((s) => s.x === x && s.e === e);
  return shape
    ? { status: "ok", shape }
    : { status: "unsupported", reason: "notListed" };
}

/** Every listed shape with this steric number, in order of lone pairs. */
export function shapesForSteric(steric: number): VseprShape[] {
  return VSEPR_SHAPES.filter((s) => s.steric === steric);
}

/* ------------------------------------------------------------ AXE notation */

const SUBSCRIPTS = "₀₁₂₃₄₅₆₇₈₉";
const NOTATION = /^A(?:X(\d*))?(?:E(\d*))?$/i;
const WHITESPACE = /[\s_]/g;
const SUBSCRIPT_DIGIT = /[₀-₉]/g;

/** Write x and e as plain text: AX2E2, AX3E, AX4. */
export function notationText(x: number, e: number): string {
  return `AX${x === 1 ? "" : x}${lonePairPart(e, String(e))}`;
}

/** The "E", "E2" part of the notation; empty without lone pairs. */
function lonePairPart(e: number, digits: string): string {
  if (e === 0) {
    return "";
  }
  return e === 1 ? "E" : `E${digits}`;
}

/** Write x and e with real subscripts: AX₂E₂. */
export function notationSubscript(x: number, e: number): string {
  const sub = (n: number) =>
    String(n)
      .split("")
      .map((d) => SUBSCRIPTS[Number(d)])
      .join("");
  return `AX${x === 1 ? "" : sub(x)}${lonePairPart(e, sub(e))}`;
}

/** Read "AX2E2", "AX₂E₂", "ax3e" or "AX4" into counts; null when it is not AXE notation. */
export function parseNotation(raw: string): { x: number; e: number } | null {
  const text = raw
    .replace(WHITESPACE, "")
    .replace(SUBSCRIPT_DIGIT, (d) => String(SUBSCRIPTS.indexOf(d)));
  const match = NOTATION.exec(text);
  if (!match) {
    return null;
  }
  const hasX = text.toUpperCase().includes("X");
  const hasE = text.toUpperCase().includes("E");
  const x = hasX ? Number(match[1] || 1) : 0;
  const e = hasE ? Number(match[2] || 1) : 0;
  return { x, e };
}

/* ------------------------------------------------------------------ sketch */

export type Vec3 = readonly [number, number, number];

export interface SketchPoint {
  kind: "x" | "e";
  position: Vec3;
}

const SQRT3 = Math.sqrt(3);
const TETRA = 1 / SQRT3;
const COS30 = SQRT3 / 2;

/**
 * Corner directions of each electron geometry (unit vectors), ordered so that the last
 * slots are the ones lone pairs occupy: equatorial first in a trigonal bipyramid,
 * the two axial positions of an octahedron for two lone pairs.
 */
const SLOTS: Record<ElectronGeometryId, readonly Vec3[]> = {
  linear: [
    [1, 0, 0],
    [-1, 0, 0],
  ],
  trigonalPlanar: [
    [1, 0, 0],
    [-0.5, COS30, 0],
    [-0.5, -COS30, 0],
  ],
  tetrahedral: [
    [TETRA, TETRA, TETRA],
    [TETRA, -TETRA, -TETRA],
    [-TETRA, TETRA, -TETRA],
    [-TETRA, -TETRA, TETRA],
  ],
  trigonalBipyramidal: [
    [0, 0, 1],
    [0, 0, -1],
    [1, 0, 0],
    [-0.5, COS30, 0],
    [-0.5, -COS30, 0],
  ],
  octahedral: [
    [1, 0, 0],
    [-1, 0, 0],
    [0, 1, 0],
    [0, -1, 0],
    [0, 0, 1],
    [0, 0, -1],
  ],
};

/** Which slots hold lone pairs, by electron geometry and lone-pair count. */
function lonePairSlots(
  geometry: ElectronGeometryId,
  e: number
): readonly number[] {
  if (e === 0) {
    return [];
  }
  const count = SLOTS[geometry].length;
  if (geometry === "octahedral") {
    // One lone pair takes an axial slot; the second sits trans to it.
    return e === 1 ? [5] : [4, 5];
  }
  return Array.from({ length: e }, (_, i) => count - 1 - i);
}

/** 3-D positions of the bonded atoms and lone pairs around A at the origin. */
export function sketchPoints(shape: VseprShape): SketchPoint[] {
  const slots = SLOTS[shape.electronGeometry];
  const lone = new Set(lonePairSlots(shape.electronGeometry, shape.e));
  return slots.map((position, index) => ({
    kind: lone.has(index) ? "e" : "x",
    position,
  }));
}

export interface ProjectedPoint {
  kind: "x" | "e";
  /** Screen x and y in the unit disc, y pointing down. */
  x: number;
  y: number;
  /** Larger = nearer the viewer. */
  depth: number;
}

const DEG = Math.PI / 180;

/** Rotate about the vertical axis by `azimuth`, tilt towards the viewer, project. */
export function projectPoints(
  points: readonly SketchPoint[],
  azimuth: number,
  tilt: number
): ProjectedPoint[] {
  const cosA = Math.cos(azimuth * DEG);
  const sinA = Math.sin(azimuth * DEG);
  const cosT = Math.cos(tilt * DEG);
  const sinT = Math.sin(tilt * DEG);
  return points
    .map(({ kind, position: [px, py, pz] }) => {
      // z is "up" in the slot tables; rotate about it, then tilt about the x axis.
      const rx = px * cosA - py * sinA;
      const ry = px * sinA + py * cosA;
      return {
        kind,
        x: rx,
        y: -(pz * cosT - ry * sinT),
        depth: ry * cosT + pz * sinT,
      };
    })
    .sort((a, b) => a.depth - b.depth);
}

/** Angle in degrees between two direction vectors. */
export function angleBetween(a: Vec3, b: Vec3): number {
  const dot = a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const lengths = Math.hypot(...a) * Math.hypot(...b);
  return Math.acos(Math.max(-1, Math.min(1, dot / lengths))) / DEG;
}
